/**
 * App-wide language state for the web app.
 *
 * Resolution order: a persisted choice (localStorage) → the browser
 * language → the default (FR). The on-device value drives the UI
 * instantly. Server-side locale sync is intentionally omitted in the
 * boilerplate — wire it to your own procedure if you need emails to
 * follow the choice.
 */
import type { Locale } from "@gately/i18n";
import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

import { DEFAULT_LOCALE, locales } from "@gately/i18n";

const STORAGE_KEY = "gately.locale";

function isLocale(s: string): s is Locale {
  return s in locales;
}

function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isLocale(stored)) return stored;
  } catch {
    // localStorage unavailable — fall through to browser/default.
  }
  if (
    typeof navigator !== "undefined" &&
    navigator.language.toLowerCase().startsWith("en")
  ) {
    return "en";
  }
  return DEFAULT_LOCALE;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (next: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale);

  const setLocale = (next: Locale) => {
    if (next === locale) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Best-effort persistence; in-memory state still drives the UI.
    }
  };

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useLocale must be used within <LocaleProvider>");
  }
  return ctx;
}
